import React, { useMemo, useState } from 'react';
import { Flex } from '@fluentui/react-northstar';
import {
    Button,
    Caption,
    Grid,
    Header,
    pxArrayToRem,
    pxToRem,
    Subheader,
    useAppDisplayModeContext,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import type { AwardProps } from '@modules/page-builder/component/detail/banner-awards/types';
import * as S from './Awards.style';

interface AwardsProps {
    awards: AwardProps[];
}

const Awards = ({ awards }: AwardsProps) => {
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();
    const theme = useTheme();
    const [awardsPage, setAwardsPage] = useState(1);
    // First page shows 4, subsequent pages show 10 (desktop) or 4 (mobile).
    const visibleAwardsCount = useMemo(
        () => 4 + (mobile ? 4 : 10) * (Math.max(1, awardsPage) - 1),
        [mobile, awardsPage]
    );
    const visibleAwards = useMemo(
        () => awards.slice(0, visibleAwardsCount),
        [awards, visibleAwardsCount]
    );

    return (
        <div className={css(S.Container)}>
            {mobile ? (
                <Header
                    variant="05"
                    as="h2"
                    variables={{
                        fontSize: pxToRem(16),
                        ...theme.siteVariables.textStyles.Heading.Medium65,
                    }}
                >
                    Awards and recognition
                </Header>
            ) : (
                <Header variant="03" as="h2">
                    Awards and
                    <br />
                    recognition
                </Header>
            )}
            {mobile ? (
                <Flex column className={css(S.GridMobile)}>
                    {visibleAwards.map((aw, i) => (
                        <React.Fragment key={'award-' + i}>
                            <Caption
                                variant="04"
                                weight={'regular'}
                                className={css(S.RightAlignedCaption)}
                                variables={{
                                    captionColor:
                                        theme.siteVariables.colors
                                            .charcoal[100],
                                }}
                            >
                                {aw.year}
                            </Caption>
                            <Caption
                                variant="03"
                                weight={'medium'}
                                className={css(S.RightAlignedCaption)}
                            >
                                {aw.institution}
                            </Caption>
                            <Caption
                                variant="03"
                                className={css(S.RightAlignedCaption)}
                            >
                                {aw.title}
                            </Caption>
                            <hr className={css(S.Divider)} />
                        </React.Fragment>
                    ))}
                </Flex>
            ) : (
                <Grid
                    className={css(S.Grid)}
                    columns={`${pxArrayToRem([270, 654])} 1fr`}
                >
                    {visibleAwards.map((aw, i) => (
                        <React.Fragment key={'award-' + i}>
                            <Subheader variant="01">{aw.year}</Subheader>
                            <Subheader variant="01">{aw.institution}</Subheader>
                            <Subheader variant="01">{aw.title}</Subheader>
                        </React.Fragment>
                    ))}
                </Grid>
            )}
            {visibleAwards.length < awards.length && (
                <div className={css(S.ViewMoreContainer)}>
                    <Button
                        variant="text"
                        color="primary"
                        size={18}
                        style={{
                            textDecoration: 'underline',
                            padding: pxToRem(12),
                        }}
                        variables={{
                            buttonPadding: 0,
                        }}
                        onClick={() => setAwardsPage((page) => page + 1)}
                    >
                        View more
                    </Button>
                </div>
            )}
        </div>
    );
};

export default Awards;
